import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
	FiFilter,
	FiRefreshCw,
	FiSearch,
	FiChevronLeft,
	FiChevronRight,
	FiPlus,
} from 'react-icons/fi';
import Card from '../../components/ui/card';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Label from '../../components/ui/label';
import SublyApi from '../../helpers/Api';

const SubmissionsList = () => {
	const history = useHistory();
	const location = useLocation();
	const token = useSelector((state) => state.admin.token);

	// Temporary: Use hardcoded admin token for testing
	const testToken =
		'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhZG1pbiI6InBlYW51dCIsImlzQXBwcm92ZWQiOnRydWUsImlhdCI6MTY0OTM4OTY0NX0.HhdqAZHDs0gCryngCOVCsZyqqk9xqeLmgwgkY5P7LqM';
	const actualToken = token || testToken;

	console.log('🔍 Redux token:', token ? 'present' : 'missing');
	console.log('🔍 Using token:', actualToken ? 'present' : 'missing');

	// State for submissions data
	const [submissions, setSubmissions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState('');

	// State for pagination
	const [currentPage, setCurrentPage] = useState(1);
	const [totalPages, setTotalPages] = useState(1);
	const [totalSubmissions, setTotalSubmissions] = useState(0);
	const pageSize = 10;

	// State for filtering
	const [filters, setFilters] = useState({
		form_id: '',
		status: '',
		username: '',
		start_date: '',
		end_date: '',
	});
	const [showFilters, setShowFilters] = useState(false);

	// State for search
	const [searchTerm, setSearchTerm] = useState('');
	const [searchTimeout, setSearchTimeout] = useState(null);
	const searchInputRef = useRef(null);

	// State for forms (for filter dropdown)
	const [forms, setForms] = useState([]);

	// State for users (for filter dropdown)
	const [users, setUsers] = useState([]);
	const [userSearchTerm, setUserSearchTerm] = useState('');
	const [showUserDropdown, setShowUserDropdown] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);

	// Fetch forms for filter dropdown
	useEffect(() => {
		const fetchForms = async () => {
			try {
				const response = await SublyApi.request('api/admin/forms', {}, 'get', {
					Authorization: `Bearer ${actualToken}`,
				});
				setForms(response.forms || []);
			} catch (err) {
				console.error('Error fetching forms:', err);
			}
		};

		const fetchUsers = async (search = '') => {
			try {
				const params = search ? `?search=${encodeURIComponent(search)}` : '';
				const response = await SublyApi.request(
					`api/admin/forms/users${params}`,
					{},
					'get',
					{
						Authorization: `Bearer ${actualToken}`,
					}
				);
				setUsers(response.users || []);
			} catch (err) {
				console.error('Error fetching users:', err);
			}
		};

		if (actualToken) {
			fetchForms();
			fetchUsers();
		}
	}, [actualToken]);

	// Fetch users when user search term changes
	useEffect(() => {
		if (actualToken) {
			const fetchUsers = async () => {
				try {
					const params = userSearchTerm
						? `?search=${encodeURIComponent(userSearchTerm)}`
						: '';
					const response = await SublyApi.request(
						`api/admin/forms/users${params}`,
						{},
						'get',
						{
							Authorization: `Bearer ${actualToken}`,
						}
					);
					setUsers(response.users || []);
				} catch (err) {
					console.error('Error fetching users:', err);
				}
			};

			const timeout = setTimeout(fetchUsers, 300); // Debounce user search
			return () => clearTimeout(timeout);
		}
	}, [userSearchTerm, actualToken]);

	// Fetch submissions
	const fetchSubmissions = async (
		page = 1,
		currentFilters = filters,
		search = searchTerm
	) => {
		console.log('🔍 Debug: fetchSubmissions called');
		console.log('🔍 Admin token available:', actualToken ? 'YES' : 'NO');
		console.log(
			'🔍 Token value:',
			actualToken ? actualToken.substring(0, 20) + '...' : 'undefined'
		);

		setLoading(true);
		setError('');

		try {
			// Handle special "pending" status filter
			const processedFilters = { ...currentFilters };
			if (processedFilters.status === 'pending') {
				// Remove the pending status and let backend handle both submitted and in-review
				delete processedFilters.status;
				// Add a special parameter to indicate we want pending submissions
				processedFilters.pending = 'true';
			}

			const params = {
				page: page.toString(),
				limit: pageSize.toString(),
				...Object.fromEntries(
					Object.entries(processedFilters).filter(([_, value]) => value)
				),
			};

			// Add search parameter if provided
			if (search && search.trim()) {
				params.search = search.trim();
			}

			const queryParams = new URLSearchParams(params);

			console.log(
				'🔍 Making API call to:',
				`api/admin/forms/submissions?${queryParams}`
			);
			console.log(
				'🔍 Using token:',
				actualToken ? 'token present' : 'NO TOKEN'
			);

			const response = await SublyApi.request(
				`api/admin/forms/submissions?${queryParams}`,
				{},
				'get',
				{ Authorization: `Bearer ${actualToken}` }
			);

			console.log('✅ API Success - Response:', response);

			if (response.submissions) {
				setSubmissions(response.submissions);
				setTotalSubmissions(response.total || 0);
				setTotalPages(Math.ceil((response.total || 0) / pageSize));
			} else {
				// Handle the case where API returns array directly
				setSubmissions(response || []);
				setTotalSubmissions(response?.length || 0);
				setTotalPages(1);
			}
		} catch (err) {
			console.error('❌ API Error Details:');
			console.error('❌ Error object:', err);
			console.error('❌ Error type:', typeof err);
			console.error('❌ Error message:', err?.message);
			console.error('❌ Error response:', err?.response);

			// Handle different types of error objects
			const errorMessage =
				err?.message ||
				err?.error?.message ||
				err?.toString() ||
				'Failed to fetch submissions';
			setError(errorMessage);
			setSubmissions([]);
		} finally {
			setLoading(false);
		}
	};

	// Initial fetch
	useEffect(() => {
		if (actualToken) {
			fetchSubmissions(currentPage, filters, searchTerm);
		}
	}, [actualToken, currentPage]);

	// Handle URL parameters for filtering and highlighting
	useEffect(() => {
		const urlParams = new URLSearchParams(location.search);
		const filterParam = urlParams.get('filter');
		const highlightParam = urlParams.get('highlight');

		// Handle filter parameter (e.g., ?filter=submitted or ?filter=pending)
		if (filterParam && filterParam !== filters.status) {
			// Handle special "pending" filter that shows both submitted and in-review
			if (filterParam === 'pending') {
				// For pending, we'll use a special filter approach
				setFilters((prev) => ({ ...prev, status: 'pending' }));
			} else {
				setFilters((prev) => ({ ...prev, status: filterParam }));
			}
			setShowFilters(true); // Show filters when coming from notification
		}

		// Handle highlight parameter (e.g., ?highlight=123)
		if (highlightParam) {
			// Scroll to highlighted submission after data loads
			setTimeout(() => {
				const element = document.getElementById(`submission-${highlightParam}`);
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'center' });
					// Add temporary highlight effect
					element.classList.add('bg-yellow-50', 'border-yellow-200');
					setTimeout(() => {
						element.classList.remove('bg-yellow-50', 'border-yellow-200');
					}, 3000);
				}
			}, 500);
		}
	}, [location.search]);

	// Refetch when filters change due to URL parameters
	useEffect(() => {
		if (actualToken) {
			fetchSubmissions(1, filters, searchTerm);
			setCurrentPage(1);
		}
	}, [filters.status]); // Only watch status filter for URL changes

	// Handle search with debouncing
	const handleSearch = (value) => {
		setSearchTerm(value);

		// Clear existing timeout
		if (searchTimeout) {
			clearTimeout(searchTimeout);
		}

		// Set new timeout for debounced search
		const timeout = setTimeout(() => {
			setCurrentPage(1);
			fetchSubmissions(1, filters, value).then(() => {
				// Restore focus to search input after search completes
				if (searchInputRef.current) {
					searchInputRef.current.focus();
				}
			});
		}, 500); // 500ms delay

		setSearchTimeout(timeout);
	};

	// Restore focus when component updates and search input should be focused
	useEffect(() => {
		if (
			searchTerm &&
			searchInputRef.current &&
			document.activeElement !== searchInputRef.current
		) {
			searchInputRef.current.focus();
		}
	}, [submissions, searchTerm]);

	// Handle clicking outside user dropdown
	useEffect(() => {
		const handleClickOutside = (event) => {
			if (showUserDropdown && !event.target.closest('.relative')) {
				setShowUserDropdown(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [showUserDropdown]);

	// Handle filter changes
	const handleFilterChange = (filterName, value) => {
		const newFilters = { ...filters, [filterName]: value };
		setFilters(newFilters);

		// Apply filters immediately
		setCurrentPage(1);
		fetchSubmissions(1, newFilters, searchTerm);
	};

	// Handle user selection
	const handleUserSelect = (user) => {
		setSelectedUser(user);
		setShowUserDropdown(false);
		setUserSearchTerm('');
		handleFilterChange('username', user.username);
	};

	// Handle user search
	const handleUserSearch = (value) => {
		setUserSearchTerm(value);
		setShowUserDropdown(true);
	};

	// Clear filters
	const handleClearFilters = () => {
		const clearedFilters = {
			form_id: '',
			status: '',
			username: '',
			start_date: '',
			end_date: '',
		};
		setFilters(clearedFilters);
		setSearchTerm('');
		setSelectedUser(null);
		setUserSearchTerm('');
		setShowUserDropdown(false);
		setCurrentPage(1);
		fetchSubmissions(1, clearedFilters, '');
		setShowFilters(false);
	};

	// Handle page change
	const handlePageChange = (newPage) => {
		if (newPage >= 1 && newPage <= totalPages) {
			setCurrentPage(newPage);
		}
	};

	// Refresh submissions
	const handleRefresh = () => {
		fetchSubmissions(currentPage, filters, searchTerm);
	};

	// Navigate to submission detail
	const handleViewSubmission = (submissionId) => {
		history.push(`/admin/submissions/${submissionId}`);
	};

	// Format date for display
	const formatDate = (dateString) => {
		try {
			return new Date(dateString).toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit',
			});
		} catch (error) {
			return 'Invalid Date';
		}
	};

	// Get status badge color
	const getStatusBadgeColor = (status) => {
		switch (status?.toLowerCase()) {
			case 'submitted':
				return 'bg-gray-100 text-gray-700';
			case 'in-review':
				return 'bg-yellow-50 text-yellow-700';
			case 'approved':
				return 'bg-green-50 text-green-700';
			case 'tasked':
				return 'bg-green-50 text-green-700';
			case 'rejected':
				return 'bg-red-50 text-red-700';
			default:
				return 'bg-gray-100 text-gray-700';
		}
	};

	// Render filters panel
	const renderFilters = () => (
		<Card className='mb-6'>
			<Card.Header>
				<div className='flex items-center justify-between'>
					<h3 className='text-lg font-montserrat-medium text-clientBlack'>Filters</h3>
					<Button
						variant='ghost'
						size='sm'
						onClick={() => setShowFilters(false)}>
						×
					</Button>
				</div>
			</Card.Header>
			<Card.Content>
				<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'>
					{/* Form Filter */}
					<div>
						<Label htmlFor='form_filter' className='font-montserrat-medium'>Form</Label>
						<select
							id='form_filter'
							aria-label='Filter by form'
							value={filters.form_id}
							onChange={(e) => handleFilterChange('form_id', e.target.value)}
							className='w-full mt-1 border border-gray-300 rounded-sm px-3 py-2 text-sm font-montserrat-regular text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary transition-colors duration-200 bg-white'>
							<option value=''>All Forms</option>
							{forms.map((form) => (
								<option key={form.id} value={form.id}>
									{form.name}
								</option>
							))}
						</select>
					</div>

					{/* Status Filter */}
					<div>
						<Label htmlFor='status_filter' className='font-montserrat-medium'>Status</Label>
						<select
							id='status_filter'
							aria-label='Filter by status'
							value={filters.status}
							onChange={(e) => handleFilterChange('status', e.target.value)}
							className='w-full mt-1 border border-gray-300 rounded-sm px-3 py-2 text-sm font-montserrat-regular text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary/40 focus:border-primary transition-colors duration-200 bg-white'>
							<option value=''>All Statuses</option>
							<option value='pending'>Pending (Submitted + In Review)</option>
							<option value='submitted'>Submitted</option>
							<option value='in-review'>In Review</option>
							<option value='approved'>Approved</option>
							<option value='rejected'>Rejected</option>
						</select>
					</div>

					{/* User Filter */}
					<div className='relative'>
						<Label htmlFor='user_filter' className='font-montserrat-medium'>User</Label>
						<div className='relative'>
							<Input
								id='user_filter'
								type='text'
								aria-label='Search and filter by user'
								value={
									selectedUser ? selectedUser.display_name : userSearchTerm
								}
								onChange={(e) => {
									if (selectedUser) {
										setSelectedUser(null);
										handleFilterChange('username', '');
									}
									handleUserSearch(e.target.value);
								}}
								onFocus={() => setShowUserDropdown(true)}
								placeholder='Search users...'
								className='w-full font-montserrat-regular text-sm'
							/>
							{selectedUser && (
								<button
									type='button'
									onClick={() => {
										setSelectedUser(null);
										setUserSearchTerm('');
										setShowUserDropdown(false);
										handleFilterChange('username', '');
									}}
									className='absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600'>
									×
								</button>
							)}
						</div>

						{/* User Dropdown */}
						{showUserDropdown && (userSearchTerm || !selectedUser) && (
							<div className='absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-sm shadow-lg max-h-60 overflow-y-auto'>
								{users.length > 0 ? (
									users.map((user) => (
										<button
											key={user.username}
											type='button'
											onClick={() => handleUserSelect(user)}
											className='w-full px-3 py-2 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none'>
											<div className='font-montserrat-medium text-sm text-gray-900'>
												{user.first_name} {user.last_name}
											</div>
											<div className='text-xs font-montserrat-regular text-gray-500'>
												{user.email} • {user.username}
											</div>
										</button>
									))
								) : (
									<div className='px-3 py-2 text-gray-500 text-sm font-montserrat-regular'>
										{userSearchTerm
											? 'No users found'
											: 'Start typing to search users...'}
									</div>
								)}
							</div>
						)}
					</div>

					{/* Start Date Filter */}
					<div>
						<Label htmlFor='start_date_filter' className='font-montserrat-medium'>Start Date</Label>
						<Input
							id='start_date_filter'
							type='date'
							value={filters.start_date}
							onChange={(e) => handleFilterChange('start_date', e.target.value)}
							className='font-montserrat-regular text-sm'
						/>
					</div>

					{/* End Date Filter */}
					<div>
						<Label htmlFor='end_date_filter' className='font-montserrat-medium'>End Date</Label>
						<Input
							id='end_date_filter'
							type='date'
							value={filters.end_date}
							onChange={(e) => handleFilterChange('end_date', e.target.value)}
							className='font-montserrat-regular text-sm'
						/>
					</div>
				</div>

				<div className='flex justify-end space-x-2 mt-4'>
					<Button variant='outline' onClick={handleClearFilters} className='font-montserrat-medium text-sm'>
						Clear All
					</Button>
				</div>
			</Card.Content>
		</Card>
	);

	// Render pagination
	const renderPagination = () => {
		if (totalPages <= 1) return null;

		// Generate page numbers to show
		const getPageNumbers = () => {
			const pages = [];
			const maxPagesToShow = 5;

			if (totalPages <= maxPagesToShow) {
				// Show all pages if total is small
				for (let i = 1; i <= totalPages; i++) {
					pages.push(i);
				}
			} else {
				// Show pages around current page
				let start = Math.max(1, currentPage - 2);
				let end = Math.min(totalPages, currentPage + 2);

				// Adjust if we're near the beginning or end
				if (currentPage <= 3) {
					end = Math.min(totalPages, 5);
				}
				if (currentPage >= totalPages - 2) {
					start = Math.max(1, totalPages - 4);
				}

				for (let i = start; i <= end; i++) {
					pages.push(i);
				}
			}

			return pages;
		};

		return (
			<nav aria-label='Pagination' className='flex flex-col sm:flex-row items-center justify-between mt-6 gap-4'>
				<div className='text-xs font-montserrat-regular text-gray-400 tracking-wide'>
					Showing {(currentPage - 1) * pageSize + 1} to{' '}
					{Math.min(currentPage * pageSize, totalSubmissions)} of{' '}
					{totalSubmissions} submissions
				</div>

				<div className='flex items-center space-x-1'>
					{/* First page */}
					{currentPage > 3 && totalPages > 5 && (
						<>
							<Button
								variant='outline'
								size='sm'
								onClick={() => handlePageChange(1)}
								className='px-3 py-2 font-montserrat-medium text-sm'>
								1
							</Button>
							{currentPage > 4 && <span className='px-2 font-montserrat-regular text-gray-400' aria-hidden='true'>...</span>}
						</>
					)}

					{/* Previous button */}
					<Button
						variant='outline'
						size='sm'
						onClick={() => handlePageChange(currentPage - 1)}
						disabled={currentPage === 1}
						aria-label='Previous page'
						className='px-2 py-2'>
						<FiChevronLeft className='h-4 w-4' />
					</Button>

					{/* Page numbers */}
					{getPageNumbers().map((pageNum) => (
						<Button
							key={pageNum}
							variant={pageNum === currentPage ? 'primary' : 'outline'}
							size='sm'
							onClick={() => handlePageChange(pageNum)}
							aria-label={`Page ${pageNum}`}
							aria-current={pageNum === currentPage ? 'page' : undefined}
							className={`px-3 py-2 font-montserrat-medium text-sm ${
								pageNum === currentPage
									? 'bg-clientBlack text-white'
									: 'hover:bg-gray-50'
							}`}>
							{pageNum}
						</Button>
					))}

					{/* Next button */}
					<Button
						variant='outline'
						size='sm'
						onClick={() => handlePageChange(currentPage + 1)}
						disabled={currentPage === totalPages}
						aria-label='Next page'
						className='px-2 py-2'>
						<FiChevronRight className='h-4 w-4' />
					</Button>

					{/* Last page */}
					{currentPage < totalPages - 2 && totalPages > 5 && (
						<>
							{currentPage < totalPages - 3 && (
								<span className='px-2 font-montserrat-regular text-gray-400' aria-hidden='true'>...</span>
							)}
							<Button
								variant='outline'
								size='sm'
								onClick={() => handlePageChange(totalPages)}
								className='px-3 py-2 font-montserrat-medium text-sm'>
								{totalPages}
							</Button>
						</>
					)}
				</div>
			</nav>
		);
	};

	if (loading && submissions.length === 0) {
		return (
			<div className='container mx-auto px-4 sm:px-6 py-8 sm:py-10'>
				<div className='flex flex-col justify-center items-center h-64'>
					<div className='animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary'></div>
					<p className='text-xs font-montserrat-medium uppercase tracking-widest text-gray-400 mt-4'>Loading Submissions</p>
				</div>
			</div>
		);
	}

	return (
		<div className='container mx-auto px-4 sm:px-6 py-8 sm:py-10'>
			{/* Header */}
			<div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-8'>
				<div>
					<h1 className='text-xl md:text-2xl font-glacial uppercase tracking-wider text-clientBlack'>Form Submissions</h1>
					<p className='text-sm font-montserrat-regular text-gray-400 mt-1'>
						Manage and review submitted forms
					</p>
				</div>

				<div className='flex flex-wrap lg:flex-nowrap items-center gap-2 w-full lg:w-auto'>
					{/* Search Bar */}
					<div className='relative'>
						<FiSearch className='absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400' />
						<Input
							type='text'
							placeholder='Search submissions...'
							aria-label='Search submissions'
							value={searchTerm}
							onChange={(e) => handleSearch(e.target.value)}
							className='pl-10 pr-4 py-2 w-full sm:w-64 font-montserrat-regular text-sm'
							ref={searchInputRef}
						/>
					</div>

					<Button
						variant='outline'
						onClick={() => setShowFilters(!showFilters)}
						className='flex items-center font-montserrat-medium text-sm'>
						<FiFilter className='h-4 w-4 mr-2' />
						Filters
						{(Object.values(filters).some((v) => v) || searchTerm) && (
							<span className='ml-1 bg-primary text-white text-xs rounded-sm px-1.5 py-0.5'>
								{Object.values(filters).filter((v) => v).length +
									(searchTerm ? 1 : 0)}
							</span>
						)}
					</Button>

					<Button
						variant='outline'
						onClick={handleRefresh}
						disabled={loading}
						className='flex items-center font-montserrat-medium text-sm'>
						<FiRefreshCw
							className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
						/>
						Refresh
					</Button>

					<Button
						variant='primary'
						onClick={() => history.push('/admin/submissions/create')}
						className='flex items-center font-montserrat-medium uppercase tracking-wider text-sm px-5 whitespace-nowrap'>
						<FiPlus className='h-4 w-4 mr-2' />
						New Submission
					</Button>
				</div>
			</div>

			{/* Filters */}
			{showFilters && renderFilters()}

			{/* Error Display */}
			{error && (
				<div className='bg-red-50 border-l-4 border-red-500 text-red-900 px-4 py-4 rounded-sm mb-6 flex items-start justify-between'>
					<div>
						<p className='font-montserrat-medium text-sm'>Something went wrong</p>
						<p className='font-montserrat-regular text-sm mt-1'>{error}</p>
					</div>
					<Button
						variant='outline'
						size='sm'
						onClick={handleRefresh}
						className='ml-4 flex-shrink-0 border-red-300 text-red-900 hover:bg-red-100 font-montserrat-medium text-xs'>
						Try Again
					</Button>
				</div>
			)}

			{/* Submissions List */}
			<Card>
				<Card.Header>
					<div className='flex items-center justify-between'>
						<h2 className='text-lg font-montserrat-medium text-clientBlack'>
							Submissions ({totalSubmissions})
						</h2>
						{loading && (
							<div className='animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-primary'></div>
						)}
					</div>
				</Card.Header>

				<Card.Content>
					{submissions.length === 0 ? (
						<div className='text-center py-12'>
							<div className='w-16 h-px bg-primary mx-auto mb-8' />
							<h3 className='text-xl font-glacial text-clientBlack mb-2'>
								No submissions found
							</h3>
							<p className='font-montserrat-regular text-sm text-gray-400 max-w-xs mx-auto'>
								{Object.values(filters).some((v) => v)
									? 'Try adjusting your filters to see more results.'
									: 'No form submissions have been received yet.'}
							</p>
						</div>
					) : (
						<div className='overflow-x-auto'>
							<table className='w-full min-w-[700px]' aria-label='Form submissions'>
								<thead>
									<tr className='border-b border-gray-200 bg-gray-50/50'>
										<th className='text-left py-3 px-4 text-xs font-montserrat-medium uppercase tracking-wide text-gray-500'>
											Form
										</th>
										<th className='text-left py-3 px-4 text-xs font-montserrat-medium uppercase tracking-wide text-gray-500'>
											Submitted By
										</th>
										<th className='text-left py-3 px-4 text-xs font-montserrat-medium uppercase tracking-wide text-gray-500'>
											Status
										</th>
										<th className='text-left py-3 px-4 text-xs font-montserrat-medium uppercase tracking-wide text-gray-500'>
											Submitted At
										</th>
										<th className='text-right py-3 px-4 text-xs font-montserrat-medium uppercase tracking-wide text-gray-500'>
											<span className='sr-only'>Actions</span>
										</th>
									</tr>
								</thead>
								<tbody>
									{submissions.map((submission) => (
										<tr
											key={submission.id}
											id={`submission-${submission.id}`}
											onClick={() => handleViewSubmission(submission.id)}
											role='link'
											tabIndex={0}
											onKeyDown={(e) => {
												if (e.key === 'Enter' || e.key === ' ') {
													e.preventDefault();
													handleViewSubmission(submission.id);
												}
											}}
											className='group border-b border-gray-100 hover:bg-gray-50/70 transition-colors duration-150 cursor-pointer'>
											<td className='py-3 px-4'>
												<div className='font-montserrat-medium text-clientBlack text-sm'>
													{submission.form_name}
												</div>
											</td>
											<td className='py-3 px-4'>
												<div>
													<div className='font-montserrat-medium text-clientBlack text-sm'>
														{submission.first_name} {submission.last_name}
													</div>
													<div className='text-xs font-montserrat-regular text-gray-400'>
														{submission.email}
													</div>
												</div>
											</td>
											<td className='py-3 px-4'>
												<span
													className={`inline-flex items-center px-2 py-0.5 rounded-sm text-xs font-montserrat-medium ${getStatusBadgeColor(
														submission.status
													)}`}>
													{submission.status || 'submitted'}
												</span>
											</td>
											<td className='py-3 px-4'>
												<div className='text-sm font-montserrat-regular text-gray-500'>
													{formatDate(submission.submitted_at)}
												</div>
											</td>
											<td className='py-3 px-4 text-right'>
												<FiChevronRight className='h-4 w-4 text-gray-300 group-hover:text-clientBlack transition-colors duration-150 inline-block' />
											</td>
										</tr>
									))}
								</tbody>
							</table>
						</div>
					)}
				</Card.Content>
			</Card>

			{/* Pagination */}
			{renderPagination()}
		</div>
	);
};

export default SubmissionsList;
